import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import {
  ConfirmationModal,
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  LinkButtonTo,
  ListItem,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { TimeOffPolicyAccrualSummary } from "@/pages/settings/time-off/policy/components/TimeOffPolicyAccrualSummary.tsx";
import { parseSalaryValue } from "@/helpers/utils.helper.ts";
import {
  accrualWithDateTypes,
  accrualWithTwoDateTypes,
  createDayOfMonthChangeHandler,
  getDayOfMonthOptions,
  getStartAmountOptions,
} from "@/pages/settings/time-off/policy/time-off-policy.types.ts";
import {
  ApiTimeOffAccrualFrequencyTypes,
  ApiTimeOffDateType,
  ApiTimeOffPolicyAccrualAtType,
  ApiTimeOffPolicyMilestone,
  getAccrualAtTypes,
  getAccrualFrequencyTypes,
  getCarryoverExpiringDateTypes,
} from "@/types/time-off/time-off-policy.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface TimeOffMilestoneEditModalProps extends ModalProps {
  milestone?: ApiTimeOffPolicyMilestone;
  index?: number;
  onModified: (milestone: ApiTimeOffPolicyMilestone) => void;
  onDelete: () => void;
  isHours: boolean;
}

export function TimeOffMilestoneEditModal({
  milestone,
  onModified,
  onDelete,
  isHours,
  ...props
}: TimeOffMilestoneEditModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { close, openModal } = useModals();
  const [startAmountSearch, setStartAmountSearch] = useState("");

  const TimeOffMilestoneObject = z.object({
    start_date_type: z.enum(ApiTimeOffDateType),
    start_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(
        z
          .number({ error: t("errors:field_required") })
          .int(t("api_errors:must_be_integer"))
          .min(0, t("api_errors:must_be_positive"))
      ),

    start_balance_enabled: z.boolean(),
    start_balance_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(z.number()),

    accrual_frequency_type: z.enum(ApiTimeOffAccrualFrequencyTypes),
    accrual_frequency_date_first: z.date().nullable().optional(),
    accrual_frequency_date_second: z.date().nullable().optional(),
    accrual_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(z.number()),

    accrual_at_type: z.enum(ApiTimeOffPolicyAccrualAtType),

    accrual_cap_enabled: z.boolean(),
    accrual_cap_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(z.number()),
  });

  type MilestoneForm = z.output<typeof TimeOffMilestoneObject>;

  const formHook = useForm<MilestoneForm>({
    mode: "onSubmit",
    resolver: zodResolver(TimeOffMilestoneObject) as Resolver<MilestoneForm>,
    defaultValues: {
      start_amount: 0,
      start_date_type: ApiTimeOffDateType.month,
      start_balance_amount: 0,
      start_balance_enabled: false,
      accrual_cap_enabled: false,
      accrual_cap_amount: 0,
      accrual_frequency_type: ApiTimeOffAccrualFrequencyTypes.monthly,
      accrual_amount: 0,
      accrual_at_type: ApiTimeOffPolicyAccrualAtType.start_of_accrual_period,
    },
  });

  const {
    watch,
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = formHook;

  const onSubmit = handleSubmit((data) => {
    const milestonePayload: ApiTimeOffPolicyMilestone = {
      title: "",
      start_date_type: data.start_date_type,
      start_amount: data.start_amount,
      start_balance_amount: data.start_balance_amount,
      accrual_frequency_type: data.accrual_frequency_type,
      accrual_amount: data.accrual_amount,
      accrual_at_type: data.accrual_at_type,
      accrual_cap_enabled: data.accrual_cap_enabled,
      accrual_cap_amount: data.accrual_cap_amount,
    };

    if (withDateInAccrual) {
      milestonePayload.accrual_frequency_data = {
        date_first: data.accrual_frequency_date_first,
      };

      if (withTwoDateInAccrual)
        milestonePayload.accrual_frequency_data.date_second = data.accrual_frequency_date_second;
    }

    onModified(milestonePayload);
    close(id);
  });

  const startDateType = watch("start_date_type");
  const startBalanceEnabled = watch("start_balance_enabled");
  const accrualCapEnabled = watch("accrual_cap_enabled");
  const accrualFrequencyType = watch("accrual_frequency_type");
  const accrualAmount = watch("accrual_amount");
  const accrualAtType = watch("accrual_at_type");

  const withDateInAccrual = accrualWithDateTypes.includes(accrualFrequencyType);
  const withTwoDateInAccrual = accrualWithTwoDateTypes.includes(accrualFrequencyType);

  useEffect(() => {
    if (!withDateInAccrual) return;
    const jan1 = new Date(new Date().getFullYear(), 0, 1);
    if (!getValues("accrual_frequency_date_first")) {
      setValue("accrual_frequency_date_first", jan1);
    }
    if (withTwoDateInAccrual && !getValues("accrual_frequency_date_second")) {
      setValue("accrual_frequency_date_second", jan1);
    }
  }, [withDateInAccrual, withTwoDateInAccrual, getValues, setValue]);

  const startAmountOptions = useMemo(() => getStartAmountOptions(startDateType), [startDateType]);
  const dayOfMonthOptions = getDayOfMonthOptions();
  const accrualFrequencyTypeOptions = getAccrualFrequencyTypes();
  const accrualAtTypeOptions = getAccrualAtTypes();
  const carryoverExpiringDateTypes = getCarryoverExpiringDateTypes();

  useEffect(() => {
    if (!milestone) return;

    reset({
      start_amount: milestone.start_amount,
      start_date_type: milestone.start_date_type,
      start_balance_amount: Number(milestone.start_balance_amount),
      start_balance_enabled: milestone.start_balance_amount > 0,
      accrual_cap_enabled: milestone.accrual_cap_enabled,
      accrual_cap_amount: Number(milestone.accrual_cap_amount),
      accrual_amount: milestone.accrual_amount,
      accrual_frequency_type: milestone.accrual_frequency_type,
      accrual_at_type: milestone.accrual_at_type,
      accrual_frequency_date_first: milestone.accrual_frequency_data?.date_first,
      accrual_frequency_date_second: milestone.accrual_frequency_data?.date_second,
    });
  }, [milestone]);

  const handleDelete = () => {
    openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          onDelete();
          close(id);
        } catch {
          return;
        }
      },
    });
  };

  return (
    <Modal
      {...props}
      title={milestone ? t("time_off:milestone_edit") : t("time_off:milestone_new")}
      withCloser={true}
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} onClick={onSubmit}>
            {t("common:save")}
          </Button>
          <Button
            variant={"plain"}
            size={"lg"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        <Stack gap={"md"}>
          <Stack gap={"md"} direction={"horizontal"} items={"center"}>
            <Text>{t("time_off:milestone_start_text_starts_in")}</Text>
            <Controller
              control={control}
              name={"start_date_type"}
              render={({ field: { value, onChange } }) => (
                <DropdownMenu
                  trigger={
                    <Button rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}>
                      {carryoverExpiringDateTypes.find((type) => type.id === value)?.title ?? value}
                    </Button>
                  }
                >
                  {carryoverExpiringDateTypes.map((option) => (
                    <DropdownMenuItem
                      key={String(option.id)}
                      title={option.title}
                      isSelected={value === option.id}
                      onClick={() => onChange(option.id)}
                    />
                  ))}
                </DropdownMenu>
              )}
            />
            <Text>{t("time_off:milestone_start_text_number")}</Text>
            <Controller
              control={control}
              name={"start_amount"}
              render={({ field: { value, onChange } }) => (
                <DropdownMenu
                  trigger={
                    <Button rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}>
                      {String(value)}
                    </Button>
                  }
                >
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Input
                      type={"number"}
                      label={t("common:search")}
                      placeholder={t("common:search_start_typing")}
                      value={startAmountSearch}
                      onChange={(e) => setStartAmountSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </Box>

                  {startAmountOptions
                    .filter((option) => option.title.includes(startAmountSearch))
                    .map((option) => (
                      <DropdownMenuItem
                        key={option.id}
                        title={option.title}
                        isSelected={String(value) === option.id}
                        onClick={() => onChange(Number(option.id))}
                      />
                    ))}
                </DropdownMenu>
              )}
            />
            <Text>{t("time_off:milestone_start_text_from_hire_date") + "."}</Text>
          </Stack>
          <Box variant={"border"}>
            <Title header={t("time_off:accrue")} size={"xs"} paddingBottom={true} />
            <Stack gap={"md"} direction={"horizontal"} className={"grid md:grid-cols-3"}>
              <Box>
                <Input
                  type={"text"}
                  {...register("accrual_amount")}
                  error={errors.accrual_amount?.message}
                  label={isHours ? t("time_off:hours") : t("time_off:days")}
                />
              </Box>
              <Box>
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("common:period")}
                      options={accrualFrequencyTypeOptions}
                      emptyTitle={t("common:select")}
                      value={value}
                      onChange={onChange}
                      error={errors.accrual_frequency_type?.message}
                    />
                  )}
                  control={control}
                  name={"accrual_frequency_type"}
                />
              </Box>
              <Box>
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("time_off:accrual_at_type_title", {
                        units: isHours
                          ? t("time_off:hours_genitive").toLowerCase()
                          : t("time_off:days_genitive").toLowerCase(),
                      })}
                      options={accrualAtTypeOptions}
                      emptyTitle={t("common:select")}
                      value={value}
                      onChange={onChange}
                      error={errors.accrual_at_type?.message}
                    />
                  )}
                  control={control}
                  name={"accrual_at_type"}
                />
              </Box>
            </Stack>
            {withDateInAccrual && (
              <Stack gap={"md"} direction={"horizontal"} className={"grid md:grid-cols-3"}>
                {(accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.monthly ||
                  accrualFrequencyType ===
                    ApiTimeOffAccrualFrequencyTypes.monthly_annual_proportional) && (
                  <Stack gap={"md"}>
                    <Box>
                      <Controller
                        control={control}
                        name={"accrual_frequency_date_first"}
                        render={({ field: { value, onChange } }) => {
                          const selectedDay = value?.getDate() || 1;

                          return (
                            <Select
                              label={t("common:day_of_month")}
                              options={dayOfMonthOptions}
                              emptyTitle={t("common:select")}
                              value={selectedDay}
                              onChange={createDayOfMonthChangeHandler(value, onChange)}
                              error={errors.accrual_frequency_date_first?.message}
                            />
                          );
                        }}
                      />
                    </Box>
                  </Stack>
                )}
                {accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.semimonthly && (
                  <>
                    <Box>
                      <Controller
                        control={control}
                        name={"accrual_frequency_date_first"}
                        render={({ field: { value, onChange } }) => {
                          const selectedDay = value?.getDate() || 1;

                          return (
                            <Select
                              label={t("common:first_day")}
                              options={dayOfMonthOptions}
                              emptyTitle={t("common:select")}
                              value={selectedDay}
                              onChange={createDayOfMonthChangeHandler(value, onChange)}
                              error={errors.accrual_frequency_date_first?.message}
                            />
                          );
                        }}
                      />
                    </Box>
                    <Box>
                      <Controller
                        control={control}
                        name={"accrual_frequency_date_second"}
                        render={({ field: { value, onChange } }) => {
                          const selectedDay = value?.getDate() || 1;

                          return (
                            <Select
                              label={t("common:day_of_month")}
                              options={dayOfMonthOptions}
                              emptyTitle={t("common:select")}
                              value={selectedDay}
                              onChange={createDayOfMonthChangeHandler(value, onChange)}
                              error={errors.accrual_frequency_date_first?.message}
                            />
                          );
                        }}
                      />
                    </Box>
                  </>
                )}
                {(accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.semiannual ||
                  accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.yearly) && (
                  <>
                    <Box>
                      <Controller
                        control={control}
                        name={"accrual_frequency_date_first"}
                        render={({ field: { value, onChange } }) => (
                          <Datepicker
                            showYearDropdown={false}
                            value={value}
                            onChange={onChange}
                            label={
                              accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.semiannual
                                ? t("common:first_date")
                                : t("common:date")
                            }
                            error={errors.accrual_frequency_date_first?.message}
                          />
                        )}
                      />
                    </Box>
                    {accrualFrequencyType === ApiTimeOffAccrualFrequencyTypes.semiannual && (
                      <Box>
                        <Controller
                          control={control}
                          name={"accrual_frequency_date_second"}
                          render={({ field: { value, onChange } }) => (
                            <Datepicker
                              showYearDropdown={false}
                              value={value}
                              onChange={onChange}
                              label={t("common:second_date")}
                              error={errors.accrual_frequency_date_second?.message}
                            />
                          )}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            )}
            <Stack className={"mt-4"}>
              <TimeOffPolicyAccrualSummary
                isHours={isHours}
                accrualAmount={accrualAmount}
                accrualFrequencyType={accrualFrequencyType}
                accrualAtType={accrualAtType}
              />
            </Stack>
          </Box>
        </Stack>
        <Stack>
          <Title header={t("common:settings")} size={"xs"} paddingBottom={true} paddingTop={true} />
          <Stack gap={"md"}>
            <Box>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                    size={"sm"}
                    title={t("time_off:milestone_start_balance_title")}
                    description={t("time_off:milestone_start_balance_caption") + "."}
                  />
                )}
                control={control}
                name={"start_balance_enabled"}
              />
              {startBalanceEnabled && (
                <Box className={"pl-9 w-88"}>
                  <Input
                    type={"text"}
                    {...register("start_balance_amount")}
                    error={errors.start_balance_amount?.message}
                    label={t("time_off:milestone_start_balance_lable", {
                      units: isHours
                        ? t("time_off:hours").toLowerCase()
                        : t("time_off:days").toLowerCase(),
                    })}
                  />
                </Box>
              )}
            </Box>
            <Box>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                    size={"sm"}
                    title={t("time_off:accrual_cap_title")}
                    description={t("time_off:accrual_cap_yes_caption") + "."}
                  />
                )}
                control={control}
                name={"accrual_cap_enabled"}
              />
              {accrualCapEnabled && (
                <Box className={"pl-9 w-88"}>
                  <Input
                    type={"text"}
                    {...register("accrual_cap_amount")}
                    error={errors.accrual_cap_amount?.message}
                    label={t("time_off:accrual_cap_yes_label", {
                      units: isHours
                        ? t("time_off:hours").toLowerCase()
                        : t("time_off:days").toLowerCase(),
                    })}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
        {milestone && (
          <Stack>
            <Title
              header={t("common:delete")}
              size={"xs"}
              className={"text-secondary"}
              paddingBottom={true}
              paddingTop={true}
            />
            <hr className="border-dark/10" />
            <ListItem
              title={t("time_off:milestone_delete_title")}
              caption={t("time_off:milestone_delete_caption")}
              valueSlot={
                <LinkButtonTo onClick={handleDelete} variant={"danger"} className={"shrink-0"}>
                  {t("common:delete") + "..."}
                </LinkButtonTo>
              }
            ></ListItem>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
